import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { IconCloseLarge } from '../../../base/icons/svg';
import Button from '../../../base/ui/components/web/Button';
import ClickableIcon from '../../../base/ui/components/web/ClickableIcon';
import { BUTTON_TYPES } from '../../../base/ui/constants.web';
import { closeAiNotesPanel, finalizeAiNotes, refreshPartialAiNotes, startAiNotesAutoSummary } from '../../actions';
import { useSidePanelEmbed } from '../../../side-panel/SidePanelEmbedContext';
import { AI_NOTES_FEATURE_KEY, AI_NOTES_SUMMARY_MODE_FINAL, MS_PER_MINUTE, MS_PER_SECOND } from '../../constants';
import { getAiNotesMeetingId, isAiNotesPanelVisible } from '../../functions';
const useStyles = makeStyles()((theme, { embedded }) => {
    return {
        pane: {
            backgroundColor: theme.palette.ui01,
            flexShrink: embedded ? undefined : 0,
            width: embedded ? '100%' : '315px',
            zIndex: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            color: theme.palette.text01,
            borderLeft: embedded ? 'none' : `1px solid ${theme.palette.ui04}`,
            ...(!embedded && {
                '@media (max-width: 580px)': {
                    height: '100dvh',
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    top: 0,
                    width: '100%',
                    zIndex: 302
                }
            })
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            flexShrink: 0
        },
        title: {
            ...theme.typography.heading6,
            margin: 0
        },
        body: {
            flex: 1,
            overflowY: 'auto',
            padding: '0 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
        },
        overviewCard: {
            backgroundColor: theme.palette.ui02,
            borderRadius: '12px',
            padding: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flexShrink: 0
        },
        overviewTitle: {
            ...theme.typography.bodyShortBold
        },
        overviewText: {
            ...theme.typography.bodyShortRegular,
            whiteSpace: 'pre-wrap',
            color: theme.palette.text02
        },
        aiMeta: {
            ...theme.typography.labelRegular,
            color: theme.palette.text03
        },
        assistantBlock: {
            ...theme.typography.bodyShortRegular,
            color: theme.palette.text02,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px'
        },
        statusRow: {
            ...theme.typography.bodyShortRegular,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px',
            color: theme.palette.text01
        },
        statusLink: {
            color: theme.palette.action01,
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline'
            },
            '&:disabled': {
                opacity: 0.5,
                cursor: 'default',
                textDecoration: 'none'
            }
        },
        sectionTitle: {
            ...theme.typography.bodyShortBold,
            marginTop: '4px'
        },
        list: {
            margin: 0,
            paddingLeft: '18px',
            ...theme.typography.bodyShortRegular,
            color: theme.palette.text02
        },
        footer: {
            flexShrink: 0,
            padding: '12px 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            borderTop: `1px solid ${theme.palette.ui04}`
        },
        footerHint: {
            ...theme.typography.labelRegular,
            color: theme.palette.text03
        }
    };
});
/**
 * 格式化倒计时剩余时间文案用数值.
 *
 * @param {number} remainingMs - Remaining ms.
 * @returns {{ minutes: number, seconds: number }}
 */
function splitRemaining(remainingMs) {
    const totalSec = Math.ceil(remainingMs / MS_PER_SECOND);
    const minutes = Math.floor(totalSec / (MS_PER_MINUTE / MS_PER_SECOND));
    const seconds = totalSec % (MS_PER_MINUTE / MS_PER_SECOND);
    return { minutes, seconds };
}
/**
 * 格式化「内容由 AI 生成」时间戳.
 *
 * @param {IAiNotesSummary} entrySummary - Summary.
 * @param {Function} t - i18n t.
 * @returns {string}
 */
function formatAiGeneratedLabel(entrySummary, t) {
    if (entrySummary.updatedAt) {
        return t('aiNotes.aiGeneratedAt', {
            time: new Date(entrySummary.updatedAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
            })
        });
    }
    return t('aiNotes.aiGenerated');
}
/**
 * 单条时间线卡片（一段一次生成）.
 *
 * @param {Object} props - Props.
 * @returns {JSX.Element}
 */
function TimelineEntryCard({ classes, entry, t }) {
    const { summary } = entry;
    const isFinal = summary.summaryMode === AI_NOTES_SUMMARY_MODE_FINAL;
    const title = isFinal ? t('aiNotes.finalSegmentTitle') : t('aiNotes.meetingOverview');
    return (React.createElement("div", { className: classes.overviewCard },
        React.createElement("div", { className: classes.overviewTitle }, title),
        summary.overview ? (React.createElement("div", { className: classes.overviewText }, summary.overview)) : (React.createElement("div", { className: classes.overviewText }, t('aiNotes.summarizingInProgress'))),
        Boolean(summary.decisions?.length) && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classes.sectionTitle }, t('aiNotes.decisions')),
            React.createElement("ul", { className: classes.list }, summary.decisions.map((item, index) => (React.createElement("li", { key: `${entry.id}-d-${index}` }, item)))))),
        Boolean(summary.actionItems?.length) && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classes.sectionTitle }, t('aiNotes.actionItems')),
            React.createElement("ul", { className: classes.list }, summary.actionItems.map((item, index) => (React.createElement("li", { key: `${entry.id}-a-${index}` }, [item.owner, item.task, item.due].filter(Boolean).join(' · '))))))),
        React.createElement("div", { className: classes.aiMeta }, formatAiGeneratedLabel(summary, t))));
}
/**
 * 「更多 → AI纪要」侧栏面板：助手说明在上，每次生成向下追加一段.
 *
 * @returns {JSX.Element | null}
 */
const AiNotesPanel = () => {
    const { embedded } = useSidePanelEmbed();
    const { classes } = useStyles({ embedded });
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const bodyRef = useRef(null);
    const visible = useSelector(isAiNotesPanelVisible);
    const meetingId = useSelector(getAiNotesMeetingId);
    const requestingSubtitles = useSelector((state) => Boolean(state['features/subtitles']?._requestingSubtitles));
    const { timeline, loading, nextSummaryAt } = useSelector((state) => state[AI_NOTES_FEATURE_KEY] || {
        summary: null,
        timeline: [],
        loading: false,
        error: null,
        panelVisible: false,
        nextSummaryAt: null
    });
    const [now, setNow] = useState(Date.now());
    const entries = Array.isArray(timeline) ? timeline : [];
    useEffect(() => {
        if (!visible || typeof nextSummaryAt !== 'number') {
            return;
        }
        const id = window.setInterval(() => setNow(Date.now()), MS_PER_SECOND);
        return () => window.clearInterval(id);
    }, [visible, nextSummaryAt]);
    // 新段落追加后滚到底部，保证最新一段可见
    useEffect(() => {
        if (!visible || !bodyRef.current || entries.length === 0) {
            return;
        }
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [visible, entries.length]);
    const onClose = useCallback(() => {
        dispatch(closeAiNotesPanel());
    }, [dispatch]);
    const onSummarizeNow = useCallback(() => {
        dispatch(refreshPartialAiNotes({ force: true }));
        dispatch(startAiNotesAutoSummary());
    }, [dispatch]);
    const onFinalize = useCallback(() => {
        dispatch(finalizeAiNotes());
    }, [dispatch]);
    if (!visible) {
        return null;
    }
    const remainingMs = typeof nextSummaryAt === 'number'
        ? Math.max(0, nextSummaryAt - now)
        : null;
    const { minutes, seconds } = remainingMs === null
        ? { minutes: 0, seconds: 0 }
        : splitRemaining(remainingMs);
    let statusText = t('aiNotes.listeningIdle');
    if (requestingSubtitles && remainingMs !== null) {
        statusText = minutes > 0
            ? t('aiNotes.listeningCountdownMin', { minutes, seconds })
            : t('aiNotes.listeningCountdownSec', { seconds });
    }
    else if (requestingSubtitles) {
        statusText = t('aiNotes.listening');
    }
    return (React.createElement("aside", { "aria-label": t('aiNotes.panelTitle'), className: classes.pane },
        !embedded && (React.createElement("div", { className: classes.header },
            React.createElement("h2", { className: classes.title }, t('aiNotes.panelTitle')),
            React.createElement(ClickableIcon, { accessibilityLabel: t('aiNotes.close'), icon: IconCloseLarge, onClick: onClose }))),
        React.createElement("div", { className: classes.body, ref: bodyRef },
            React.createElement("div", { className: classes.assistantBlock },
                React.createElement("div", null, t('aiNotes.assistantHello')),
                React.createElement("div", null, t('aiNotes.assistantAfterMeeting'))),
            entries.map(entry => (React.createElement(TimelineEntryCard, { classes: classes, entry: entry, key: entry.id, t: t }))),
            React.createElement("div", { className: classes.statusRow },
                React.createElement("span", null, statusText),
                React.createElement("button", { className: classes.statusLink, disabled: loading, onClick: onSummarizeNow, type: 'button' }, t('aiNotes.summarizeNow'))),
            React.createElement("div", { className: classes.aiMeta },
                t('aiNotes.meetingId'),
                ": ",
                meetingId,
                loading ? ` · ${t('aiNotes.loading')}` : '')),
        React.createElement("div", { className: classes.footer },
            React.createElement("div", { className: classes.footerHint }, t('aiNotes.footerHint')),
            React.createElement(Button, { accessibilityLabel: t('aiNotes.finalize'), disabled: loading, labelKey: 'aiNotes.finalize', onClick: onFinalize, type: BUTTON_TYPES.PRIMARY }))));
};
export default AiNotesPanel;
